import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'

import { Tooltip } from 'react-tippy'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal, Table, Button, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

import { File } from 'src/lib/interfaces/File.interface'

import FileViewerModal from './FileViewer.modal'

import FileType from './FileType.component'
import previewable from 'src/utils/previewable'
import { getFileExtension } from '../utils/files'
import { formatFileName, formatFileSize } from 'src/lib/helpers/format'

import * as appActions from 'src/store/app/actions'

const moment = require('moment')
const map = require('lodash/map')
const sortBy = require('lodash/sortBy')
const includes = require('lodash/includes')

export type FileListViewMode = 'card' | 'table'

export interface FileListProps {
  error?: any
  entity: string
  files?: File[]
  className?: string
  canDelete?: boolean
  onDelete?: () => void
  entityId: string|number
  onTryAgain?: () => void
  viewMode?: FileListViewMode
  fileWrapperClassName?: string
  deleteEntityFile: (entity: string, entityId: string|number, fileKey: string, next?: (err?: any, downloadedFile?: any) => void) => void
  downloadEntityFile: (entity: string, entityId: string|number, fileKey: string, next: (err?: any, downloadedFile?: any) => void) => void
}

export interface FileListState {
  deletingFile: boolean,
  currentOpenFile?: File
  fileToBeDeleted?: File
}

class FileList extends React.Component<FileListProps, FileListState> {
  state = {
    deletingFile: false,
    currentOpenFile: undefined,
    fileToBeDeleted: undefined
  }

  isFileConfidential = (fileKey: string) => {
    return includes(fileKey, 'confidential/')
  }

  toggleModalOpen = () => {
    this.setState({ currentOpenFile: undefined })
  }

  onFetchFile = (fileKey: string, next: (err?: any, file?: any) => void) => {
    const { entity, entityId, downloadEntityFile } = this.props

    downloadEntityFile(entity, entityId, fileKey, next)
  }

  onDownloadFile = (fileKey: string) => {
    this.onFetchFile(fileKey, (err?: any, file?: any) => {
      if (file) {
        window.open(file.fileURL, '_blank')
      }
    })
  }

  isFilePreviewable(file: File) {
    const extension = getFileExtension(file)
    return previewable[extension] || false
  }

  handleDeleteFile = file => {
    const { entity, entityId, onDelete, deleteEntityFile } = this.props

    console.log('handleDeleteFile', file)
    this.setState({ deletingFile: true }, () => {
      deleteEntityFile(entity, entityId, file.key, (err?: any) => {
        this.setState({ deletingFile: false, fileToBeDeleted: undefined })
        
        if (onDelete) {
          onDelete()
        }
      })
    })
  }

  renderFileDeletionConfirmationDialog = () => {
    const { deletingFile, fileToBeDeleted } = this.state

    const onClose = () => this.setState({ fileToBeDeleted: undefined })

    return (
      <Modal isOpen={!!fileToBeDeleted} toggle={onClose}>
        <ModalHeader toggle={onClose}>Delete File</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this file?
        </ModalBody>
        <ModalFooter>
          <Button disabled={deletingFile} color="light" onClick={onClose}>Cancel</Button>
          <Button color="primary" disabled={deletingFile} onClick={() => this.handleDeleteFile(fileToBeDeleted)}>
            {deletingFile && <FontAwesomeIcon spin icon={["fas", "spinner"]} className="ggcollapsible-icon mr-3" />}
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  render() {
    const { currentOpenFile } = this.state
    const { error, files, viewMode, className, canDelete, onTryAgain, fileWrapperClassName } = this.props

    if (!!error) {
      return (
        <div className="filelist--error">
          <div className="filelist--errorcontainer">
            <span>Error fetching files</span>
            <Button size="sm" onClick={onTryAgain}>Try again</Button>
          </div>
        </div>
      )
    }

    const orderedFiles = sortBy(files, file => this.isFileConfidential(file.key))

    return (
      <div className={className}>
        {this.renderFileDeletionConfirmationDialog()}
        <FileViewerModal open={!!currentOpenFile} onToggle={this.toggleModalOpen} fetchFile={this.onFetchFile} file={currentOpenFile} />

        {(!viewMode || viewMode === 'table')
          ? (
            <Table hover className="table table-striped">
              <thead>
                <tr>
                  <td className="pr-1 py-2" />
                  <td className="font-weight-bold px-1 py-2">Type</td>
                  <td className="font-weight-bold px-1 py-2">Size</td>
                  <td className="font-weight-bold px-1 py-2">Name</td>
                  <td className="text-right font-weight-bold pr-2 px-1 py-2">Date</td>
                </tr>
              </thead>
              <tbody>
                {map(orderedFiles, file => (
                  <tr
                    key={file.key}
                    className={classnames({ 'cursor-pointer': this.isFilePreviewable(file) })}
                    onClick={() => {
                      if (this.isFilePreviewable(file)) {
                        this.setState({ currentOpenFile: file })
                      }
                    }}
                  >
                    <td className="d-flex align-items-center p-1">
                      <div onClick={e => {
                        e.stopPropagation()
                        this.onDownloadFile(file.key)
                      }}>
                        <FontAwesomeIcon
                          size="lg"
                          icon={["fas", "download"]}
                          className="cursor-pointer mr-2"
                        />
                      </div>

                      {canDelete &&
                        <div onClick={e => {
                          e.stopPropagation()
                          this.setState({ fileToBeDeleted: file })
                        }}>
                          <FontAwesomeIcon
                            size="lg"
                            icon={["fas", "trash"]}
                            className="cursor-pointer"
                          />
                        </div>
                      }

                      {this.isFileConfidential(file.key) && (
                        <Tooltip className="d-flex" trigger="mouseenter" html={
                          <div className="ggmouseover d-flex flex-column align-items-center p-2">
                            <span style={{ fontSize: '13px' }}>This is a <b>confidential</b> file</span>
                          </div>
                        }>
                          <FontAwesomeIcon
                            className="ml-2"
                            icon={["fas", "lock"]}
                            style={{ fontSize: '17px' }}
                          />
                        </Tooltip>
                      )}
                    </td>
                    <td className="text-uppercase p-1">
                      {getFileExtension(file)}
                    </td>
                    <td className="p-1">{formatFileSize(file.size)}</td>
                    <td><b className="p-1">{formatFileName(file)}</b></td>
                    <td className="text-right p-1 pr-2">{moment(file.lastModified).format('MMM DD YYYY')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : map(files, (file, i) => (
              <div className={fileWrapperClassName} key={i}>
                <div className="list-group-item d-flex">
                  <FileType type={getFileExtension(file)} confidential={this.isFileConfidential(file.key)} />
                  <div className="flex-grow-1 min-width-0 ml-3">
                    <div className="text-truncate">
                      <strong className="text-uppercase">{formatFileName(file)}</strong>
                    </div>
                    <div className="mt-1">{moment(file.lastModified).format('MMM DD YYYY')} | {formatFileSize(file.size)}</div>
                    <div className="mt-3">
                      {this.isFilePreviewable(file)
                        ? <button onClick={() => this.setState({ currentOpenFile: file })} className="btn btn-primary mr-2">Open</button>
                        : <button onClick={() => this.onDownloadFile(file.key)} className="btn btn-primary mr-2">Download</button>
                      }
                    </div>
                  </div>
                </div>
              </div>
            ))
          }
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  deleteEntityFile: (entity: string, entityId: string|number, fileKey: string, next?: (err?: any, file?: any) => void) =>
    dispatch(appActions.deleteEntityFile(entity, entityId, fileKey, next)),
  downloadEntityFile: (entity: string, entityId: string|number, fileKey: string, next: (err?: any, file?: any) => void) =>
    dispatch(appActions.downloadEntityFile(entity, entityId, fileKey, next))
})

export default connect(null, mapDispatchToProps)(FileList)



// WEBPACK FOOTER //
// ./src/common/FileList.component.tsx